import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AdvancedPriceChartWidget from '../DashboardBuilder/AdvancedPriceChartWidget';
import IndicatorPickerDropdown from '../DashboardBuilder/IndicatorPickerDropdown';
import IndicatorDefaultsBadge from '../DashboardBuilder/IndicatorDefaultsBadge';
import IndicatorSettingsModal from '../DashboardBuilder/IndicatorSettingsModal';
import {
  buildTimeframeIndicatorConfig,
  getEffectiveSettings,
  migrateSettings,
  setCustomSettings,
  resetIndicator,
  resetAll,
  restoreIndicator,
  restoreAll,
} from '../../utils/timeframeIndicatorSettings';
import {
  buildTargetOverlays,
  computeChartHeight,
  VALUE_HEADER_HEIGHT,
} from '../../utils/zeroDteChartOverlays';

// localStorage keys — one triple per chart surface, matching the
// researchPriceChart_* / premiumChartModal_* / expandedCard_* convention.
// Also registered in ErrorBoundary's recoverable-cache list.
const STORAGE_INDICATORS = 'zeroDteChart_indicators';
const STORAGE_BOLLINGER = 'zeroDteChart_showBollinger';
const STORAGE_SETTINGS = 'zeroDteChart_indicatorSettings';

// 1D/1W are intraday (5-minute and 30-minute bars, regular hours only) and are
// only available for plain US equities — the provider has no intraday coverage
// for indices or exchange-suffixed symbols.
const RANGE_OPTIONS = [
  ['1d', '1D', true],
  ['1w', '1W', true],
  ['1m', '1M'],
  ['3m', '3M'],
  ['6m', '6M'],
  ['1y', '1Y'],
  ['2y', '2Y'],
  ['5y', '5Y'],
];

const INDEX_TICKERS = ['VIX', 'DJI', 'IXIC', 'GSPC', 'RUT', 'FTSE', 'N225', 'HSI'];

const supportsIntraday = (ticker) => {
  if (!ticker) return false;
  const upper = String(ticker).toUpperCase();
  return !upper.includes('.') && !INDEX_TICKERS.includes(upper);
};

/**
 * Price history for the ticker being analysed in the 0DTE screener.
 *
 * Lives in the page's right panel and sticks to the viewport while expanded, so
 * the user can see where their target price sits relative to actual history
 * while filling in the form. Clicking the price line sets the target.
 *
 * Collapsed means UNMOUNTED, not hidden: the chart widget has no cache and
 * refetches on mount, so an unmounted card costs nothing.
 */
export default function UnderlyingChartCard({
  ticker = null,
  strikeMode = 'bands',
  targetPrice = '',
  bufferPct = null,
  currentPrice = null,
  // Bumped by the page whenever a NEW ticker is chosen — force-expands the card.
  expandSignal = 0,
  onPickTargetPrice = null,
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [dateRange, setDateRange] = useState('3m');

  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_INDICATORS);
      return saved ? JSON.parse(saved) : ['bollinger'];
    } catch {
      return ['bollinger'];
    }
  });

  const [showBollinger, setShowBollinger] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_BOLLINGER);
      // Default ON for this surface (the others default off).
      return saved == null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  // Per-timeframe settings store: { __v, byTimeframe, lastCustom }.
  // migrateSettings lifts the older flat shape onto the daily timeframes.
  const [settingsStore, setSettingsStore] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_SETTINGS);
      return migrateSettings(saved ? JSON.parse(saved) : null);
    } catch {
      return migrateSettings(null);
    }
  });

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Persist
  useEffect(() => {
    try { localStorage.setItem(STORAGE_INDICATORS, JSON.stringify(indicators)); } catch {}
  }, [indicators]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_BOLLINGER, String(showBollinger)); } catch {}
  }, [showBollinger]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_SETTINGS, JSON.stringify(settingsStore)); } catch {}
  }, [settingsStore]);

  // Selecting a new ticker opens the card. Reacting to the change (not to a
  // boolean) keeps a later manual collapse sticky until the next selection.
  useEffect(() => {
    if (expandSignal > 0) setCollapsed(false);
  }, [expandSignal]);

  // A ticker without intraday coverage can't stay on 1D/1W.
  const intradayAvailable = supportsIntraday(ticker);
  useEffect(() => {
    if (!intradayAvailable && (dateRange === '1d' || dateRange === '1w')) {
      setDateRange('3m');
    }
  }, [intradayAvailable, dateRange]);

  const handleIndicatorToggle = useCallback((baseIndicator, customSettings) => {
    setIndicators(prev => (
      prev.includes(baseIndicator)
        ? prev.filter(i => i !== baseIndicator)
        : [...prev, baseIndicator]
    ));
    if (customSettings) {
      setSettingsStore(prev => setCustomSettings(prev, dateRange, baseIndicator, customSettings));
    }
    if (baseIndicator === 'bollinger') setShowBollinger(true);
  }, [dateRange]);

  const handleOpenSettings = useCallback((indicator) => {
    setSelectedIndicator(indicator);
    setShowSettingsModal(true);
  }, []);

  const handleSaveSettings = useCallback((indicator, settings) => {
    setSettingsStore(prev => setCustomSettings(prev, dateRange, indicator, settings));
  }, [dateRange]);

  // Reset drops the override but keeps it restorable; restore puts back the
  // most recent custom values saved for this timeframe.
  const handleResetIndicator = useCallback((indicator) => {
    setSettingsStore(prev => resetIndicator(prev, dateRange, indicator));
  }, [dateRange]);
  const handleRestoreIndicator = useCallback((indicator) => {
    setSettingsStore(prev => restoreIndicator(prev, dateRange, indicator));
  }, [dateRange]);
  const handleResetAll = useCallback(() => {
    setSettingsStore(prev => resetAll(prev, dateRange, indicators));
  }, [dateRange, indicators]);
  const handleRestoreAll = useCallback(() => {
    setSettingsStore(prev => restoreAll(prev, dateRange, indicators));
  }, [dateRange, indicators]);

  // The widget's memo comparator checks onPricePick by identity, so keep this
  // reference stable and read the latest callback through a ref.
  const pickRef = useRef(onPickTargetPrice);
  useEffect(() => { pickRef.current = onPickTargetPrice; }, [onPickTargetPrice]);
  const handlePricePick = useCallback((price) => {
    if (pickRef.current) pickRef.current(price);
  }, []);

  // Resolves defaults -> timeframe overrides -> the user's custom values, and
  // returns null when everything is at the plain defaults so daily requests
  // stay byte-identical (and don't trigger a needless refetch).
  const indicatorConfig = useMemo(
    () => buildTimeframeIndicatorConfig(indicators, dateRange, settingsStore.byTimeframe),
    [indicators, dateRange, settingsStore]
  );

  const { lines, bands } = useMemo(
    () => buildTargetOverlays({ strikeMode, targetPrice, bufferPct, currentPrice }),
    [strikeMode, targetPrice, bufferPct, currentPrice]
  );

  const chartHeight = computeChartHeight(indicators, viewportHeight, {
    valueHeaderPx: VALUE_HEADER_HEIGHT,
  });

  const target = parseFloat(targetPrice);
  const hasTarget = strikeMode === 'target' && Number.isFinite(target) && target > 0;

  return (
    <div
      className={`zerodte-chart-card ${collapsed ? '' : 'expanded is-sticky'}`}
      data-tour="underlying-chart"
    >
      <div className="collapsible-header" onClick={() => setCollapsed(prev => !prev)}>
        <div className="collapsible-header-left">
          <span className={`chevron ${collapsed ? '' : 'rotate-90'}`}>›</span>
          <h3>Price Chart</h3>
          {ticker
            ? <span className="zerodte-chart-ticker">{ticker}</span>
            : <span className="zerodte-chart-hint">select a ticker</span>}
          {collapsed && hasTarget && (
            <span className="zerodte-chart-target">Target ${target.toFixed(2)}</span>
          )}
        </div>

        {/* The whole header toggles the card, so the controls must not bubble */}
        {!collapsed && ticker && (
          <div className="zerodte-chart-actions" onClick={(e) => e.stopPropagation()}>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="text-xs bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
              title="Date range"
            >
              {RANGE_OPTIONS
                .filter(([, , isIntraday]) => !isIntraday || intradayAvailable)
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
            <IndicatorPickerDropdown
              indicators={indicators}
              indicatorSettings={settingsStore.byTimeframe[dateRange] || {}}
              onToggleIndicator={handleIndicatorToggle}
              onEditIndicator={handleOpenSettings}
              showBollinger={showBollinger}
              onShowBollingerChange={setShowBollinger}
              align="right"
            />
            <IndicatorDefaultsBadge
              indicators={indicators}
              timeframe={dateRange}
              byTimeframe={settingsStore.byTimeframe}
              lastCustom={settingsStore.lastCustom}
              onResetIndicator={handleResetIndicator}
              onRestoreIndicator={handleRestoreIndicator}
              onResetAll={handleResetAll}
              onRestoreAll={handleRestoreAll}
              align="right"
            />
          </div>
        )}
      </div>

      {!collapsed && (ticker ? (
        <>
          <p className="zerodte-chart-help">
            Click the price line to set that level as your Target Price.
          </p>
          <div className="zerodte-chart-body" style={{ height: chartHeight }}>
            <AdvancedPriceChartWidget
              ticker={ticker}
              dateRange={dateRange}
              indicators={indicators}
              indicatorConfig={indicatorConfig}
              isLinked={false}
              showBollinger={showBollinger}
              setShowBollinger={setShowBollinger}
              priceReferenceLines={lines}
              priceReferenceBands={bands}
              onPricePick={handlePricePick}
              showValueHeader
              showValueFooter
            />
          </div>
        </>
      ) : (
        <div className="zerodte-chart-empty">
          Select a ticker to see its price history.
        </div>
      ))}

      {showSettingsModal && selectedIndicator && (
        <IndicatorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => { setShowSettingsModal(false); setSelectedIndicator(null); }}
          onSave={handleSaveSettings}
          indicator={selectedIndicator}
          currentSettings={getEffectiveSettings(selectedIndicator, dateRange, settingsStore.byTimeframe)}
        />
      )}
    </div>
  );
}
